import { Component, Vue, Prop } from 'vue-property-decorator';
import GoogleMaterialIcon from '@/components/icons/google-material-icon/google-material-icon';
import PerformanceProgress from '@/components/charts/performance-bar/performance-bar';
import DountChart from '@/components/charts/dount-chart/dount-chart';
import { sortByProperty } from '@/utils/utils';
import { SubjectModel } from '@/models/drill-down/subject';
import { PerformanceData } from '@/models/groups/performance';
import { TimespentData, ClassTimespentData } from '@/models/groups/timespent';
import { ReportData, ReportDataDrilldown } from '@/models/groups/report';
import { appConfigService } from '@/providers/services/app/app-config';
import { ClassCompetencyData, ClassCompetencyDataDrilldown, ReportPerformanceStatsModel } from '@/models/groups/class';
import { getPercentage } from '@/helpers/number-format';

import { DEMO_HIERARCHY_POPUP_HEADS } from '@/utils/constants';
import DrilldownPerformancePullup from '../drilldown-performance-pullup/drilldown-performance-pullup';

@Component({
    name: 'drilldown-perf-pullup',
    components: {
        'material-icon': GoogleMaterialIcon,
        'performance-bar': PerformanceProgress,
        'dount-chart': DountChart,
        'drilldown-reports': DrilldownPerformancePullup,
        'pdf-generator': () => import('@/components/pdf-generator/pdf-generator'),
    },
})

export default class DrilldownPerfPullup extends Vue {

    @Prop()
    private countryData: any;
    // We have to enable once demo is over 14th May
    @Prop()
    private totalPerformance: any;
    @Prop()
    private totalCompetencyGained: any;

    @Prop()
    private performanceData!: PerformanceData;

    @Prop()
    private competencyData!: ReportData;

    @Prop()
    private classCompetencyData!: ClassCompetencyData;

    @Prop()
    private classTimespentData!: ClassTimespentData;

    @Prop()
    private timespentData!: TimespentData;

    @Prop()
    private groupType!: string;

    @Prop()
    private groupId!: string;

    @Prop()
    private isAtInitialLevel!: boolean;

    @Prop()
    private subjectData!: SubjectModel[];

    @Prop()
    private activeSubject: any;

    @Prop()
    private activeMonth!: string;

    @Prop()
    private activeYear!: string;

    @Prop()
    private isLoading!: boolean;

    @Prop()
    private competencyPerformance!: ReportPerformanceStatsModel;

    private subjectsList: any = [];

    private breadCrumb: any = [];

    private isFirstBreadCrumb: boolean = true;

    private enableRightScroll: boolean = false;

    private enableLeftScroll: boolean = false;

    private activeSortKey = null;

    private get performanceDrilldown() {
        return this.performanceData.drilldown;
    }

    private get classPefDrilldown() {
        return this.classCompetencyData.drilldown;
    }

    private get competencyDrilldown() {
        return this.competencyData.drilldown || [];
    }

    private get perfData() {
        return this.groupType === 'class' ? this.classCompetencyData : this.competencyData;
    }

    private get subSelection() {
        return this.$access.isDemoGroup
            ? DEMO_HIERARCHY_POPUP_HEADS[this.perfData.type] : this.perfData.name;
    }

    private get performanceDrilldownItem() {
        return this.competencyPerformance;
    }

    private get isSchoolLevel() {
        return this.competencyPerformance.gradeList;
    }

    private get hasReportPermission() {
        return this.$access.hasPageAccess(this.$access.menus.network, this.$access.ACL.showGPFCompReport);
    }

    private get overallCompByStatus() {
        const total = this.perfData.overallStats.totalCompetencies;
        const mastered: number = this.perfData.overallStats.overallMasteredCompetencies / total * 100;
        const inprogress: number = this.perfData.overallStats.totalInprogressCompetencies / total * 100;
        const notStarted: number = this.perfData.overallStats.totalNotstartedCompetencies / total * 100;
        return [
            {
                width: mastered > 0 ? mastered.toFixed(1) : 0,
                color: '#1d7dc2',
                label: 'Mastered',
            },
            {
                width: inprogress > 0 ? inprogress.toFixed(1) : 0,
                color: '#1aa9eb',
                label: 'In Progress',
            },
            {
                width: notStarted > 0 ? notStarted.toFixed(1) : 0,
                color: '#dcdfe0',
                label: 'Not Started',
            },
        ];

    }

    private get groupData() {
        return this.groupType === 'class' ? this.classCompetencyData : this.competencyData;
    }

    private get averagePerformance(): number {
        const averagePerformance = this.groupType === 'class' ?
            this.classCompetencyData.overallStats.overallAveragePerformance :
            this.performanceData.overallStats.averagePerformance;
        return averagePerformance ? parseFloat(averagePerformance.toFixed(2)) : 0;
    }

    private get overallMasteredCompetencies() {
        return this.groupType === 'class' ?
            this.classCompetencyData.overallStats.overallMasteredCompetencies :
            this.competencyData.overallStats.overallMasteredCompetencies;
    }

    private get totalInprogressCompetencies() {
        return this.perfData.overallStats.totalInprogressCompetencies;
    }

    private get maxTotalCompetencyInDrilldownn() {
        const drilldown: Array<ReportDataDrilldown | ClassCompetencyDataDrilldown> = this.perfData.drilldown;
        return Math.max.apply(Math, drilldown.map((drilldownItem: any) => drilldownItem.totalCompetencies));
    }

    private get averageDount() {
        return [
            {
                name: 'chart',
                value: this.averagePerformance,
                color: this.performanceColor(this.averagePerformance),
            },
            {
                name: 'chart',
                value: 100 - this.averagePerformance,
                color: '#ebeced',
            },
        ];
    }

    private get competencyDount() {
        return [{
            name: 'chart',
            value: this.perfData.overallStats.overallMasteredCompetencies,
            color: '#0a66ba',
        },
        {
            name: 'chart1',
            value: this.perfData.overallStats.totalNotstartedCompetencies,
            color: '#ebeced',
        },
        {
            name: 'chart2',
            value: this.perfData.overallStats.totalInprogressCompetencies,
            color: '#7ccff7',
        }];
    }

    private get perfCompetencyDrilldowns() {
        let drilldownList: any = [];
        const competencyDrilldown: any = this.competencyDrilldown || [];
        if (this.groupType === 'class') {
            drilldownList = this.classPefDrilldown;
        } else {
            drilldownList = this.performanceDrilldown.map((perfData) => {
                return Object.assign(competencyDrilldown.find(
                    (compData: any) => compData.id === perfData.id), perfData);
            });
        }
        sortByProperty(drilldownList, 'masteredCompetencies', 'DESC');
        return drilldownList;
    }


    get groupLabel() {
        const accessibleLevels = appConfigService.getUserGroupAccessLevel();
        const competencyData = this.groupType === 'class' ? this.classCompetencyData : this.competencyData;
        const activeLevelIndex = accessibleLevels.findIndex((level: any) => level.type === competencyData.type);
        if (this.$access.isDemoGroup) {
            return this.groupType === 'class'
                ? 'learners' : DEMO_HIERARCHY_POPUP_HEADS[accessibleLevels[activeLevelIndex + 1].type];
        }
        return this.groupType === 'class' ? 'student' : activeLevelIndex >= 0 ? accessibleLevels[activeLevelIndex + 1] ?
            accessibleLevels[activeLevelIndex + 1].name : competencyData.type : competencyData.type;
    }
    private get isDemoTenant() {
        return appConfigService.isDemoTenant();
    }
    private get isGuestUser() {
        return this.$access.isGuestUser('guest', 'guest');
    }

    private get pdfPropData() {
        return {
            groupLabel: this.groupLabel,
            activeSortKey : this.activeSortKey,
            subSelection: this.subSelection,
            isAtInitialLevel: this.isAtInitialLevel,
            activeSubject: this.activeSubject,
            performanceDrilldown: this.performanceDrilldownItem,
            activeMonth: this.activeMonth,
            activeYear: this.activeYear,
            isPdfDownload: true,
        };
    }


    @Prop({ default: false })
    private isRestrictClass!: boolean;

    private get averageCompPercentage() {
        let masterCompetencies = 0;
        let totalCompetencies = 0;
        this.perfCompetencyDrilldowns.forEach((item: any) => {
            masterCompetencies += item.masteredCompetencies;
            totalCompetencies += item.totalCompetencies;
        });
        return Math.round((masterCompetencies / totalCompetencies) * 100);
    }

    private get isClassLevel() {
        return this.groupType === 'class';
    }

    // -----------------------------------------------------------------------
    // Actions

    public onSelectSort(option: any) {
        this.activeSortKey = option.key;
      }

    private onSelectLevel(level: any) {
        this.$emit('onSelectLevel', level, this.activeSubject, 'performance');
    }

    private onGoupward() {
        this.$emit('onGoUpward', this.activeSubject, 'performance');
    }

    private onGoLevelUp() {
        this.onGoupward();
    }

    private onSelectSubject(subject: any = null) {
        this.$emit('onSelectSubject', subject);
    }

    private beforeDownload(configObj: any) {
        const { html2pdf, options, pdfContent } = configObj;
        html2pdf().set(options).from(pdfContent).toPdf().get('pdf').then((pdf: any) => {
            (this.$refs.classpdfLoader as any).isLoading = false;
        }).save();
    }

    private onGeneratePdf() {
        const classpdfLoader: any = this.$refs.classpdfLoader;
        classpdfLoader.onGeneratePdf();
    }

    // ------------------------------------------------------------------------
    // Hooks
    private created() {
        // this.activeSubject = this.subjectData.length ? this.subjectData[0] : null;
        window.scrollTo(0, 0);
    }

    private mounted() {
        this.onScrollActions();
    }

    // --------------------------------------------------------------------------
    // Methods

    private performanceColor(level: any) {
        if (level > 90) {
            return '#5b8f42';
        } else if (level > 80) {
            return '#a7cf98';
        } else if (level > 60) {
            return '#fac553';
        } else {
            return '#fa787a';
        }
    }

    private getDrilldownByStatus(perfCompDrilldown: ReportDataDrilldown) {
        // const total = this.totalCompetencyGained;
        const total = perfCompDrilldown.totalCompetencies;
        const mastered: number = Math.round(perfCompDrilldown.masteredCompetencies * 100 / total) || 0;
        const inprogress: number = Math.round(perfCompDrilldown.inprogressCompetencies * 100 / total) || 0;
        const notStarted = 100 - mastered - inprogress;
        // const notStarted: number = Math.round(perfCompDrilldown.notstartedCompetencies * 100 / total ) ;
        return [
            {
                width: notStarted,
                color: '#dcdfe0',
                label: 'Not Started',
                count: perfCompDrilldown.notstartedCompetencies,
            },
            {
                width: inprogress,
                color: '#1aa9eb',
                label: 'In Progress',
                count: perfCompDrilldown.inprogressCompetencies,
            },
            {
                width: mastered,
                color: '#1d7dc2',
                label: 'Mastered',
                count: perfCompDrilldown.masteredCompetencies,
            }];
    }

    private getPercentage(value: number, total: number) {
        return getPercentage(value, total);
    }

    private sortArray(key: string = 'fullName', list: any[] = []) {
        return list.sort((a: any, b: any) => {
            if (a[key] < b[key]) { return -1; }
            if (a[key] > b[key]) { return 1; }
            return 0;
        });
    }

    private onScrollActions() {
        const bodyContainer: null | any = this.$el.querySelector('.performance-nav-tab-panel');
        if (bodyContainer.scrollWidth > bodyContainer.clientWidth) {
            this.enableRightScroll = true;
        }
        let startPosition = bodyContainer.scrollLeft;
        bodyContainer.onscroll = () => {
            if (bodyContainer.scrollLeft > startPosition) {
                this.enableLeftScroll = true;
                const clientWidth = bodyContainer.scrollWidth - bodyContainer.scrollLeft;
                if (clientWidth === bodyContainer.clientWidth) { this.enableRightScroll = false; }
            }
            if (bodyContainer.scrollLeft < startPosition) {
                this.enableRightScroll = true;
                if (bodyContainer.scrollLeft === 0) { this.enableLeftScroll = false; }
            }
            startPosition = bodyContainer.scrollLeft;
        };
    }

    private scollWithButtons(action: any) {
        const bodyContainer: null | any = this.$el.querySelector('.performance-nav-tab-panel');
        if (action === 'Right') {
            bodyContainer.scrollLeft += 150;
            this.enableLeftScroll = true;
            const clientWidth = bodyContainer.scrollWidth - bodyContainer.scrollLeft;
            if (clientWidth === bodyContainer.clientWidth) { this.enableRightScroll = false; }
        }
        if (action === 'Left') {
            bodyContainer.scrollLeft -= 150;
            this.enableRightScroll = true;
            if (bodyContainer.scrollLeft === 0) { this.enableLeftScroll = false; }
        }
    }

}
